import React, { useState, useEffect, useCallback } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MobileNavigation } from '../components/MobileNavigation';
import { Scan, CheckCircle, XCircle, Camera, CameraSlash, Keyboard, Users, Clock, MapPin, List, UserCheck } from '@phosphor-icons/react';
import { LoadingSpinner } from '../components/ui/loading-spinner';
import { toast } from 'sonner';
import { getApiBaseUrl } from '../utils/api';

export function QRScanner() {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [manualToken, setManualToken] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [showRoster, setShowRoster] = useState(false);
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [checkingIn, setCheckingIn] = useState(null);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [sessionStats, setSessionStats] = useState({ registered: 0, checkedIn: 0 });
  const [userRole, setUserRole] = useState('staff');
  
  // Jersey number states
  const [showJerseyPrompt, setShowJerseyPrompt] = useState(false);
  const [pendingCheckin, setPendingCheckin] = useState(null);
  const [jerseyNumber, setJerseyNumber] = useState('');
  
  useEffect(() => {
    loadTodaySessions();
    fetchUserRole();
  }, []);
  
  const fetchUserRole = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const user = await response.json();
        setUserRole(user.role || 'staff');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
    }
  };
  
  useEffect(() => {
    if (selectedSession) {
      loadSessionStats(selectedSession);
    }
  }, [selectedSession]);
  
  const loadTodaySessions = async () => {
    try {
      // Get today's date in LOCAL timezone (not UTC)
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      console.log('[QR Scanner] Today (local):', today);
      
      const response = await fetch(`${getApiBaseUrl()}/api/sessions`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[QR Scanner] All sessions:', data.map(s => ({ date: s.date, time: s.start_time })));
        
        // Show today's sessions and upcoming sessions (next 7 days)
        const relevantSessions = data.filter(session => {
          // Compare dates as strings (YYYY-MM-DD format)
          return session.date >= today;
        }).sort((a, b) => {
          // Sort by date, then by start time
          if (a.date !== b.date) return a.date.localeCompare(b.date);
          return (a.start_time || '').localeCompare(b.start_time || '');
        }).slice(0, 10); // Limit to next 10 sessions
        
        console.log('[QR Scanner] Filtered sessions:', relevantSessions.map(s => ({ date: s.date, time: s.start_time })));
        
        setSessions(relevantSessions);
        
        // Auto-select if only one session today
        const todaysSessions = relevantSessions.filter(s => s.date === today);
        if (todaysSessions.length === 1) {
          setSelectedSession(todaysSessions[0].session_id);
        } else if (relevantSessions.length === 1) {
          setSelectedSession(relevantSessions[0].session_id);
        }
      } else {
        toast.error('Failed to load sessions');
      }
    } catch (error) {
      console.error('Load sessions error:', error);
      toast.error('Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };
  
  const loadSessionStats = async (sessionId) => {
    try {
      setLoadingRoster(true);
      const response = await fetch(`${getApiBaseUrl()}/api/sessions/${sessionId}/roster`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        // Handle both formats: {roster: [...]} or just [...]
        const rosterData = data.roster || data || [];
        const checkedIn = rosterData.filter(r => r.checked_in).length;
        setRoster(rosterData);
        setSessionStats({
          registered: rosterData.length,
          checkedIn: checkedIn
        });
        setRecentCheckins(rosterData.filter(r => r.checked_in).slice(-5).reverse());
      }
    } catch (error) {
      console.error('Load stats error:', error);
    } finally {
      setLoadingRoster(false);
    }
  };
  
  const handleManualCheckin = async (playerId, playerName) => {
    if (!selectedSession) {
      toast.error('Please select a session first');
      return;
    }
    
    setCheckingIn(playerId);
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/admin/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          player_id: playerId,
          session_id: selectedSession
        })
      });
      
      if (response.ok) {
        toast.success(`${playerName} checked in!`);
        loadSessionStats(selectedSession);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Check-in failed');
      }
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error('Check-in failed');
    } finally {
      setCheckingIn(null);
    }
  };
  
  const saveJerseyNumber = async () => {
    if (!pendingCheckin || !jerseyNumber.trim()) {
      setShowJerseyPrompt(false);
      setPendingCheckin(null);
      return;
    }
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/checkin/${pendingCheckin.checkin_id}/jersey`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ jersey_number: jerseyNumber.trim() })
      });
      
      if (response.ok) {
        toast.success(`Jersey #${jerseyNumber} saved for ${pendingCheckin.player_name}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save jersey number');
      }
    } catch (error) {
      console.error('Save jersey error:', error);
      toast.error('Failed to save jersey number');
    } finally {
      setShowJerseyPrompt(false);
      setPendingCheckin(null);
      setJerseyNumber('');
    }
  };
  
  const skipJerseyNumber = () => {
    setShowJerseyPrompt(false);
    setPendingCheckin(null);
    setJerseyNumber('');
  };
  
  const handleScan = useCallback(async (qrToken) => {
    if (!selectedSession) {
      toast.error('Please select a session first');
      return;
    }
    
    // Prevent duplicate scans
    if (scanResult?.status === 'processing') return;
    
    setScanResult({ status: 'processing', message: 'Processing check-in...' });
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/checkin/scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          qr_token: qrToken,
          session_id: selectedSession
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setScanResult({
          status: 'success',
          message: `${result.player_name} checked in successfully!`,
          playerName: result.player_name
        });
        toast.success(`${result.player_name} checked in!`);
        
        // Show jersey number prompt
        setPendingCheckin({
          checkin_id: result.checkin_id,
          player_name: result.player_name
        });
        setJerseyNumber('');
        setShowJerseyPrompt(true);
        
        // Update stats
        loadSessionStats(selectedSession);
        
        // Clear scan result after 3 seconds (but keep jersey prompt open)
        setTimeout(() => setScanResult(null), 3000);
      } else {
        const error = await response.json();
        setScanResult({
          status: 'error',
          message: error.detail || 'Check-in failed'
        });
        toast.error(error.detail || 'Check-in failed');
        
        setTimeout(() => setScanResult(null), 5000);
      }
    } catch (error) {
      console.error('Scan error:', error);
      setScanResult({
        status: 'error',
        message: 'Network error. Please try again.'
      });
      toast.error('Network error occurred');
      
      setTimeout(() => setScanResult(null), 5000);
    }
  }, [selectedSession, scanResult]);
  
  const handleQRScan = (result) => {
    if (result && result[0]?.rawValue) {
      handleScan(result[0].rawValue);
    }
  };
  
  const handleManualScan = () => {
    if (!manualToken.trim()) {
      toast.error('Please enter a QR token');
      return;
    }
    
    handleScan(manualToken.trim());
    setManualToken('');
  };
  
  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const time = new Date();
    time.setHours(parseInt(hours), parseInt(minutes));
    return time.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  
  const getStatusIcon = () => {
    if (scanResult?.status === 'processing') return <LoadingSpinner size="lg" />;
    if (scanResult?.status === 'success') return <CheckCircle size={64} className="text-green-500" weight="fill" />;
    if (scanResult?.status === 'error') return <XCircle size={64} className="text-red-500" weight="fill" />;
    return null;
  };
  
  const selectedSessionData = sessions.find(s => s.session_id === selectedSession);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size="xl" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background pb-20" data-testid="qr-scanner">
      {/* Jersey Number Prompt Modal */}
      {showJerseyPrompt && pendingCheckin && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-sm space-y-4">
            <div className="text-center">
              <CheckCircle size={48} className="text-green-500 mx-auto mb-2" weight="fill" />
              <h3 className="text-xl font-bold">{pendingCheckin.player_name}</h3>
              <p className="text-sm text-muted-foreground">Checked in successfully!</p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Jersey Number</label>
              <Input
                type="text"
                placeholder="Enter jersey #"
                value={jerseyNumber}
                onChange={(e) => setJerseyNumber(e.target.value)}
                className="text-center text-2xl font-bold h-14"
                autoFocus
                maxLength={3}
                inputMode="numeric"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveJerseyNumber();
                }}
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={skipJerseyNumber}
                className="flex-1"
              >
                Skip
              </Button>
              <Button
                onClick={saveJerseyNumber}
                className="flex-1 bg-primary text-white"
                disabled={!jerseyNumber.trim()}
              >
                Save #{jerseyNumber || '?'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="px-4 py-4">
          <h1 className="text-xl font-heading">QR SCANNER</h1>
          <p className="text-sm text-muted-foreground">
            Scan player QR codes for check-in
          </p>
        </div>
      </div>
      
      {/* Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Session Selection */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-lg font-heading">SELECT SESSION</CardTitle>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-4">
                <Clock size={32} className="mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">No sessions in the next 7 days</p>
              </div>
            ) : (
              <>
                <Select value={selectedSession} onValueChange={setSelectedSession}>
                  <SelectTrigger data-testid="session-select">
                    <SelectValue placeholder="Choose a session" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessions.map((session) => {
                      const sessionDate = new Date(session.date + 'T00:00:00');
                      const today = new Date();
                      today.setHours(0,0,0,0);
                      const isToday = sessionDate.toDateString() === today.toDateString();
                      const dayLabel = isToday ? 'TODAY' : sessionDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                      
                      return (
                        <SelectItem key={session.session_id} value={session.session_id}>
                          {dayLabel} • {formatTime(session.start_time)} - {session.location?.name || 'Unknown'}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                
                {selectedSessionData && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div className="p-3 bg-muted/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-primary">{sessionStats.checkedIn}</p>
                      <p className="text-xs text-muted-foreground">Checked In</p>
                    </div>
                    <div className="p-3 bg-muted/20 rounded-lg text-center">
                      <p className="text-2xl font-bold text-muted-foreground">{sessionStats.registered}</p>
                      <p className="text-xs text-muted-foreground">Registered</p>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        
        {/* Scanner */}
        {selectedSession && (
          <Card className="bg-card border-border overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-heading">
                {showRoster ? 'ROSTER CHECK-IN' : scanning ? 'SCANNING...' : 'CAMERA'}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={showRoster ? 'default' : 'outline'}
                  onClick={() => {
                    setShowRoster(!showRoster);
                    if (!showRoster) setScanning(false);
                  }}
                >
                  <List size={16} className="mr-1" />
                  Roster
                </Button>
                {!showRoster && (
                  <Button
                    size="sm"
                    variant={scanning ? 'destructive' : 'default'}
                    onClick={() => setScanning(!scanning)}
                    className={scanning ? '' : 'btn-primary'}
                  >
                    {scanning ? (
                      <>
                        <CameraSlash size={16} className="mr-1" />
                        Stop
                      </>
                    ) : (
                      <>
                        <Camera size={16} className="mr-1" />
                        Start
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Roster View */}
              {showRoster ? (
                <div className="p-4 max-h-[60vh] overflow-y-auto">
                  {loadingRoster ? (
                    <div className="flex justify-center py-8">
                      <LoadingSpinner />
                    </div>
                  ) : roster.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users size={48} className="mx-auto mb-3 opacity-50" />
                      <p>No players registered</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {roster.map((entry, index) => {
                        const player = entry.player;
                        const isCheckedIn = entry.checked_in;
                        const playerId = player?.player_id;
                        const jerseyNum = entry.jersey_number;
                        
                        return (
                          <div
                            key={playerId || index}
                            className={`p-3 rounded-lg border flex items-center justify-between ${
                              isCheckedIn 
                                ? 'bg-green-500/10 border-green-500/30' 
                                : 'bg-background/50 border-border'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                isCheckedIn ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'
                              }`}>
                                {isCheckedIn ? (
                                  jerseyNum ? (
                                    <span className="text-sm">#{jerseyNum}</span>
                                  ) : (
                                    <CheckCircle size={18} weight="bold" />
                                  )
                                ) : (
                                  <span className="text-xs">{index + 1}</span>
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{player?.name || 'Unknown'}</p>
                                <p className="text-xs text-muted-foreground">
                                  Age {player?.age}
                                  {isCheckedIn && jerseyNum && <span className="ml-2 text-green-500">Jersey #{jerseyNum}</span>}
                                </p>
                              </div>
                            </div>
                            
                            {isCheckedIn ? (
                              <span className="text-xs px-2 py-1 rounded bg-green-500/20 text-green-500 flex items-center gap-1">
                                <CheckCircle size={12} />
                                {jerseyNum ? `#${jerseyNum}` : 'Done'}
                              </span>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => handleManualCheckin(playerId, player?.name)}
                                disabled={checkingIn === playerId}
                                className="btn-primary"
                              >
                                {checkingIn === playerId ? (
                                  <LoadingSpinner size="sm" />
                                ) : (
                                  <>
                                    <UserCheck size={14} className="mr-1" />
                                    Check In
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <>
              {/* Scan Result Overlay */}
              {scanResult && (
                <div className={`absolute inset-0 z-10 flex flex-col items-center justify-center bg-background/95 ${
                  scanResult.status === 'success' ? 'bg-green-500/10' :
                  scanResult.status === 'error' ? 'bg-red-500/10' : ''
                }`}>
                  {getStatusIcon()}
                  <p className={`text-xl font-bold mt-4 text-center px-4 ${
                    scanResult.status === 'success' ? 'text-green-500' :
                    scanResult.status === 'error' ? 'text-red-500' : ''
                  }`}>
                    {scanResult.message}
                  </p>
                </div>
              )}
              
              {/* Camera View */}
              {scanning ? (
                <div className="relative aspect-square max-h-[400px] bg-black">
                  <Scanner
                    onScan={handleQRScan}
                    constraints={{
                      facingMode: 'environment'
                    }}
                    styles={{
                      container: { width: '100%', height: '100%' },
                      video: { width: '100%', height: '100%', objectFit: 'cover' }
                    }}
                    components={{
                      audio: false,
                      torch: true
                    }}
                  />
                  
                  {/* Scan Frame Overlay */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-64 h-64 border-2 border-primary rounded-lg relative">
                      <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg"></div>
                      <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-lg"></div>
                      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-lg"></div>
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg"></div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-square max-h-[400px] bg-muted/20 flex flex-col items-center justify-center">
                  <Scan size={64} className="text-muted-foreground mb-4" weight="duotone" />
                  <p className="text-muted-foreground text-center px-4">
                    Press "Start" to activate the camera and scan QR codes
                  </p>
                </div>
              )}
              
              {/* Manual Input */}
              {showManualInput && !showRoster && (
                <div className="p-4 border-t border-border">
                  <p className="text-sm text-muted-foreground mb-2">Enter QR code manually:</p>
                  <div className="flex gap-2">
                    <Input
                      value={manualToken}
                      onChange={(e) => setManualToken(e.target.value)}
                      placeholder="Enter QR token..."
                      onKeyPress={(e) => e.key === 'Enter' && handleManualScan()}
                      data-testid="manual-token-input"
                    />
                    <Button onClick={handleManualScan} className="btn-primary">
                      Submit
                    </Button>
                  </div>
                </div>
              )}
              </>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Recent Check-ins */}
        {selectedSession && recentCheckins.length > 0 && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <Users size={20} weight="duotone" className="text-green-500" />
                RECENT CHECK-INS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {recentCheckins.map((checkin, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-2 bg-green-500/10 rounded-lg"
                  >
                    <div className="flex items-center gap-2">
                      <CheckCircle size={16} className="text-green-500" weight="fill" />
                      <span className="font-medium">{checkin.player?.name}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {checkin.checkin_time ? new Date(checkin.checkin_time).toLocaleTimeString() : 'Just now'}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      
      <MobileNavigation userRole={userRole} />
    </div>
  );
}
